import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useTranslation } from "react-i18next";
import styles from "./HeadacheReport.module.css"; 
import headacheData from "../../data/headache.json";

import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// 타입 정의
interface RiskFactor {
  keyword: string;
  description: string;
}

interface ExpandedRiskFactor extends RiskFactor {
  categoryKey: string;
  priority: number;
}

interface SharedResult {
  kind: string;
  createdAt?: any;
  score: number;
  riskFactors: RiskFactor[];
  qaRiskFactors?: RiskFactor[]; 
}

interface Option { 
  optionTextKey: string;
  value: boolean | number;
  riskFactor?: RiskFactor; 
}


interface FollowUpQuestion {
  id: string;
  text_key: string;
  riskFactor?: RiskFactor;
  options?: Option[];
  followUpIfYes?: FollowUpQuestion[];
}

interface Question {
  id: string;
  type: string;
  questionTextKey: string;
  riskFactor?: RiskFactor;
  options?: Option[];
  followUpIfYes?: FollowUpQuestion[];
}


const findQuestion = (id: string, questions: (Question | FollowUpQuestion)[]): Question | FollowUpQuestion | undefined => {
  for (const q of questions) {
      if (q.id === id) return q;
      if (q.followUpIfYes) {
          const result = findQuestion(id, q.followUpIfYes as (Question | FollowUpQuestion)[]);
          if (result) return result;
      }
  }
  return undefined;
};


const getCategory = (keyword: string): string => {
  // 1. 위험 신호 (Red Flags)
  if (
    keyword.includes("갑작") ||
    keyword.includes("뻣뻣") ||
    keyword.includes("구토") ||
    keyword.includes("심해지는") ||
    keyword.includes("아침에") ||
    keyword.includes("기억력") ||
    keyword.includes("성격") ||
    keyword.includes("건망증") ||
    keyword.includes("이상 행동") ||
    keyword.includes("신체 약화") ||
    keyword.includes("언어") ||
    keyword.includes("신경") ||
    keyword.includes("한쪽에") ||
    keyword.includes("자세") ||
    keyword.includes("기침") ||
    keyword.includes("누워있을") ||
    keyword.includes("무거운")
  ) {
    return "category_red_flags";
  }

  // 2. 전신 증상 (Systemic Symptoms)
  if (
    keyword.includes("발열") ||
    keyword.includes("체중 감소") ||
    keyword.includes("식욕") ||
    keyword.includes("발진") ||
    keyword.includes("관절통") ||
    keyword.includes("여행") ||
    keyword.includes("감염")
  ) {
    return "category_systemic_symptoms";
  }

  // 3. 두통 유형 특징 (Headache Type)
  if (
    keyword.includes("빛") ||
    keyword.includes("소리") ||
    keyword.includes("메스꺼움") ||
    keyword.includes("어두운") ||
    keyword.includes("눈물") ||
    keyword.includes("충혈") ||
    keyword.includes("코막힘") ||
    keyword.includes("같은 시간") ||
    keyword.includes("눈 주변")
  ) {
    return "category_headache_type";
  }
  
  // 4. 기타 요인 (Other Factors)
  if (
    keyword.includes("진통제") ||
    keyword.includes("생리") ||
    keyword.includes("외상") ||
    keyword.includes("암") ||
    keyword.includes("면역")
  ) {
    return "category_other_factors";
  }

  return "category_other"; // 위에 해당하지 않는 경우
}

const getCategoryPriority = (categoryKey: string): number => {
  if (categoryKey === "category_red_flags") return 1;
  if (categoryKey === "category_systemic_symptoms") return 2;
  if (categoryKey === "category_headache_type") return 3;
  if (categoryKey === "category_other_factors") return 4;
  return 99; // category_other
};

const HeadacheReport: React.FC = () => {
  const { id, hospitalId } = useParams<{ id: string; hospitalId?: string }>();
  const { t } = useTranslation();
  const prefix = hospitalId ? `/${hospitalId}` : '';

  const reportContentRef = useRef<HTMLDivElement>(null); 
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const navigate = useNavigate();
  const [data, setData] = useState<SharedResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Firestore에서 결과 문서 불러오기
  useEffect(() => {
    const fetchResult = async () => {
      if (!id) {
        setError("잘못된 접근입니다.");
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, "sharedHeadacheResults", id); 
        const snap = await getDoc(docRef);

        if (snap.exists()) {
          setData(snap.data() as SharedResult);
        } else {
          setError("결과를 찾을 수 없습니다.");
        }
      } catch (err) {
        console.error("Firestore getDoc error:", err);
        setError("결과 불러오기에 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [id]);

  // PDF 생성을 위한 전체 스크롤 auto 설정
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    const root = document.getElementById('root');
    const originalRootOverflow = root ? root.style.overflow : '';

    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    if (root) root.style.overflow = 'auto';

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
      if (root) root.style.overflow = originalRootOverflow;
    };
  }, []);
  
  // 날짜 포맷 함수 
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString("ko-KR", {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(/\. /g, '.').slice(0, -1); 
  };

  // PDF 다운로드 로직 (기존 유지)
  const handleDownloadPdf = async () => {
    // 1. container와 originalWidth를 함수 스코프 최상단에서 선언
    const container = reportContentRef.current;
    
    if (!container) {
        alert("오류: 리포트 화면을 찾을 수 없습니다.");
        return;
    }

    setIsGeneratingPdf(true);
    
    // 2. originalWidth는 container가 유효할 때 한 번만 선언
    const originalWidth = container.style.width;

    try {
      // 3. 캡처를 위한 스타일 적용
      container.style.width = '720px'; 
      window.scrollTo(0, 0);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);
      const contentHeight = pdfHeight - (margin * 2); 

      // data-pdf-section="true" 속성을 가진 요소만 캡처
      const elements = Array.from(container.querySelectorAll('[data-pdf-section="true"]'));

      let currentHeight = 0; 
      let currentPage = 1; 

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLElement;
        
        // 요소 개별 캡처
        const canvas = await html2canvas(el, {
          scale: 2, 
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        } as any);

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        if (currentHeight + imgHeight > contentHeight) {
          pdf.addPage(); 
          currentPage++;
          currentHeight = 0;
        }

        pdf.addImage(imgData, 'JPEG', margin, margin + currentHeight, contentWidth, imgHeight);
        currentHeight += imgHeight + 2; 
      }
      
      // Footer 캡처
      const footer = container.querySelector('[data-pdf-footer="true"]') as HTMLElement;
      if (footer) {
         const footerCanvas = await html2canvas(footer, { scale: 2, backgroundColor: '#ffffff' } as any);
         const footerImg = footerCanvas.toDataURL('image/jpeg', 0.95);
         const footerHeight = (footerCanvas.height * contentWidth) / footerCanvas.width;

         if (currentHeight + footerHeight > contentHeight) {
            pdf.addPage();
            currentPage++; 
            currentHeight = 0;
         }
         pdf.addImage(footerImg, 'JPEG', margin, margin + currentHeight, contentWidth, footerHeight);
      }

      // 페이지 번호 추가
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150);
        pdf.text(`${i} / ${totalPages}`, pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      pdf.save('headache-report.pdf');

    } catch (error: any) {
      console.error("PDF Error:", error);
      alert(`저장 실패: ${error.message || error}`);
    } finally {
      setIsGeneratingPdf(false);
      // 4. finally 블록에서 접근 가능 (스코프 해결)
      if (container) {
        container.style.width = originalWidth; 
      }
    }
  };


  const groupedFactors = useMemo(() => {
    const factorsToUse = data?.qaRiskFactors || data?.riskFactors;
    if (!factorsToUse) return new Map<string, ExpandedRiskFactor[]>();

    const factorsWithCategory = factorsToUse.map(factor => {

      const questionId = factor.keyword; 

      let classificationKeyword = questionId; 
      const questionData = findQuestion(questionId, headacheData.questions as (Question | FollowUpQuestion)[]);
      
      if (questionData && questionData.riskFactor) {
          classificationKeyword = t(questionData.riskFactor.keyword);
      } else if (questionData && 'options' in questionData && questionData.options) {
          const riskOption = questionData.options?.find(opt => opt.riskFactor);
          if (riskOption && riskOption.riskFactor) {
             classificationKeyword = t(riskOption.riskFactor.keyword);
          }
      } else {
          classificationKeyword = factor.keyword;
      }
      
      const categoryKey = getCategory(classificationKeyword); 
      const priority = getCategoryPriority(categoryKey);
      
      return { ...factor, categoryKey, priority };
    });

    // 우선순위 정렬 적용
    factorsWithCategory.sort((a, b) => a.priority - b.priority);

    const grouped = new Map<string, ExpandedRiskFactor[]>();
    factorsWithCategory.forEach(factor => {
      const categoryKey = factor.categoryKey;
      if (!grouped.has(categoryKey)) {
        grouped.set(categoryKey, []);
      }
      grouped.get(categoryKey)?.push(factor);
    });
    
    return grouped;
  }, [data, t]); 

  // 카테고리별 위험 요소 수 계산
  const categoryRiskCounts = useMemo(() => {
    const counts = new Map<string, number>();
    groupedFactors.forEach((factors, categoryKey) => {
      counts.set(categoryKey, factors.length);
    });
    return counts;
  }, [groupedFactors]);
  
  const renderCategoryGroupContent = (
    categoryKey: string,
    factors: ExpandedRiskFactor[],
    riskCount: number
  ) => {
    let categoryTitle = "";
    switch (categoryKey) {
        case 'category_red_flags':
            categoryTitle = t('headache_report.category_red_flags_title', '응급 위험 신호 (Red Flags)');
            break;
        case 'category_systemic_symptoms':
            categoryTitle = t('headache_report.category_systemic_symptoms_title', '동반된 전신 증상');
            break;
        case 'category_headache_type':
            categoryTitle = t('headache_report.category_headache_type_title', '두통 유형 및 특징');
            break;
        case 'category_other_factors':
            categoryTitle = t('headache_report.category_other_factors_title', '기타 잠재적 위험 요인');
            break;
        default:
            categoryTitle = t('headache_report.category_other_title', '분류 외 기타');
    }

    return (
        <React.Fragment>
          {/* 1. 카테고리 제목 및 뱃지 */}
          <div className={styles.sectionHeader} data-pdf-section="true">
            <h3 className={styles.categoryTitle}>
              {categoryTitle}
            </h3>

            <span className={styles.riskBadge}>
              {t('risk_count_badge', '{{count}}개의 위험요소 식별', { count: riskCount })}
            </span>
          </div>

          {/* 2. 카테고리 설명 */}
          <p className={styles.sectionDescription} data-pdf-section="true">
            {t(`headache_report.${categoryKey}_desc`, "식별된 두통 위험요인을 자세히 살펴보세요.")}
          </p>
          
          {/* 3. 위험 요소 리스트 (실제 질문-사용자 답변으로 렌더링) */}
          <div className={styles.qaList}>
            {factors.length > 0 ? (
              factors.map((factor, idx) => {
                const questionId = factor.keyword; 
                const userAnswerText = factor.description; 

                const questionData = findQuestion(questionId, headacheData.questions as (Question | FollowUpQuestion)[]);
                
                let displayQuestionText = questionId; 
                
                if (questionData) {
                    // 1. 질문 객체를 찾았다면, 질문 키를 사용하여 번역합니다.
                    const questionKey = 'questionTextKey' in questionData 
                                        ? questionData.questionTextKey 
                                        : questionData.text_key;
                    displayQuestionText = t(questionKey, questionId); 
                } else if (factor.keyword.includes('_q')) {
                    // 2. 질문 ID처럼 보이지만 찾지 못한 경우, ID를 표시합니다.
                    displayQuestionText = questionId;
                } else {
                    // 3. 질문 ID가 아닌 경우 (즉, 원본 riskFactors), 키워드 자체를 표시합니다.
                    displayQuestionText = factor.keyword;
                }
                
                return (
                  <div key={idx} className={styles.qaItem} data-pdf-section="true">
                    <span className={styles.qaQuestion} style={{fontWeight: '700', color: '#000'}}>{displayQuestionText}</span>
                    <span className={styles.qaAnswer} style={{marginTop: '4px', fontWeight: '500'}}>{userAnswerText}</span>
                  </div>
                );
              })
            ) : (
              <p className={styles.noRiskMessage} data-pdf-section="true">{t("no_risk_factor", "식별된 위험인자 없음")}</p>
            )}
          </div>
          
        </React.Fragment>
    );
  };


  // 로딩/에러 상태 처리
  if (loading)
    return <div className={styles.loading}>⏳ {t("loading", "결과를 불러오는 중...")}</div>;
  if (error)
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={() => navigate(`${prefix}/`)}>{t("back_to_home", "홈으로")}</button>
      </div>
    );

  if (!data) return null;

  return (
    <div className={styles.reportWrapper}>
      <div className={styles.reportContainer} ref={reportContentRef}>
        
        <header className={styles.header} data-pdf-section="true">
          <div className={styles.logo}>
            <span style={{ backgroundColor: "#1a73e8" }}></span>
            <span style={{ backgroundColor: "#fbbc05" }}></span>
            <span style={{ backgroundColor: "#34a853" }}></span>
            <span style={{ backgroundColor: "#ea4335" }}></span>
          </div>
          <div className={styles.headerText}>
            <h1 className={styles.mainTitle}>BrainCheck</h1>
            <h2 className={styles.subTitle}>두통 위험요소 검사 결과</h2>
            <div className={styles.metaInfo}>
              <span>{`검사 일자 ${formatDate(data.createdAt)}`}</span>
              <span>v1.0.0</span>
              <span>{`UID ${id?.substring(0, 12) || 'N/A'}`}</span>
            </div>
          </div>
        </header>

        <section className={styles.contentSection}>
          {Array.from(groupedFactors.entries()).map(([categoryKey, factors], index) => (
            <React.Fragment key={categoryKey}>
              {index > 0 && (
                <div style={{ height: '30px', visibility: 'hidden' }} data-pdf-section="true"></div>
              )}

              <div className={styles.categoryGroup}>
                {renderCategoryGroupContent(categoryKey, factors, categoryRiskCounts.get(categoryKey) || 0)}
              </div>
            </React.Fragment>
          ))}

          {groupedFactors.size === 0 && (
             <div className={styles.categoryGroup} data-pdf-section="true">
               <p style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                 {t("no_risk_total", "검사 결과, 특별히 식별된 두통 위험 요인이 없습니다. 건강한 생활 습관을 유지하세요!")}
               </p>
             </div>
          )}
        </section>
        <div className={styles.footerInfo} data-pdf-footer="true" style={{ marginTop: '40px', textAlign: 'center', color: '#888', fontSize: '0.8rem' }}>
             BrainCheck © 2025 SHMD. All rights reserved.
        </div>
      </div>

      <div style={{ textAlign: 'center', padding: '20px' }}>
        <button 
          onClick={handleDownloadPdf} 
          className={styles.printButton}
          disabled={isGeneratingPdf}
          style={{ position: 'relative', zIndex: 9999, cursor: 'pointer' }} 
        >
          {isGeneratingPdf ? "PDF 생성 중..." : "📄 PDF로 저장 / 인쇄"}
        </button>
      </div>

    </div>
  );
};

export default HeadacheReport;